import { forwardRef, useImperativeHandle, useRef, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { useControllableState } from '../../hooks/useControllableState';
import { Icon } from '../../icons/Icon';
import { fieldAdornment, fieldButton, fieldFrame, fieldFrameSizes, fieldInner, useFieldA11y, type FieldControlProps } from '../shared/fieldStyles';
import { useDsT } from '../../services/i18n';

/**
 * Invalid fields never rely on the hairline colour alone: the two-ring focus treatment is
 * re-tinted to danger by swapping the focus-ring token, so keyboard focus repeats the error.
 * (`fieldStyles` is frozen, so this lives here and is shared by the number fields.)
 */
export const invalidFocusRing = 'data-invalid:[--zo-focus-ring-color:var(--zo-danger)]';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'value' | 'defaultValue'>,
    FieldControlProps {
  value?: string;
  defaultValue?: string;
  /** Fired with the raw string value on every change (in addition to `onChange`). */
  onValueChange?: (value: string) => void;
  /** Leading adornment (icon, unit, text). */
  prefix?: ReactNode;
  /** Trailing adornment. */
  suffix?: ReactNode;
  /** Monospace + tabular digits — for codes, LPNs, SKUs. */
  mono?: boolean;
  /** Shows a clear button while the input holds a value. */
  clearable?: boolean;
  onClear?: () => void;
  /** Stretch to the container width (default true). */
  fullWidth?: boolean;
  /** Class applied to the outer frame; `className` targets the native input. */
  frameClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    value,
    defaultValue,
    onChange,
    onValueChange,
    prefix,
    suffix,
    mono,
    clearable,
    onClear,
    fullWidth = true,
    frameClassName,
    className,
    size = 'md',
    invalid,
    readOnly,
    disabled,
    type = 'text',
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useDsT();
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
  const [current, setCurrent] = useControllableState<string>({ value, defaultValue: defaultValue ?? '', onChange: onValueChange });
  const a11y = useFieldA11y({ invalid, 'aria-describedby': ariaDescribedBy });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCurrent(e.target.value);
    onChange?.(e);
  };

  const clear = () => {
    setCurrent('');
    onClear?.();
    inputRef.current?.focus();
  };

  const showClear = clearable && !disabled && !readOnly && current.length > 0;

  return (
    <div
      className={cn(fieldFrame, fieldFrameSizes[size], invalidFocusRing, fullWidth ? 'w-full' : 'w-auto', frameClassName)}
      data-size={size}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      data-readonly={readOnly || undefined}
    >
      {prefix != null ? <span className={fieldAdornment}>{prefix}</span> : null}
      <input
        ref={inputRef}
        type={type}
        value={current}
        onChange={handleChange}
        disabled={disabled}
        readOnly={readOnly}
        className={cn(fieldInner, mono && 'font-mono tnum', className)}
        {...a11y}
        {...rest}
      />
      {showClear ? (
        <button type="button" tabIndex={-1} aria-label={t('ds.common.clear', 'Clear')} onClick={clear} className={cn(fieldButton, 'size-6')}>
          <Icon name="x" size={12} />
        </button>
      ) : null}
      {suffix != null ? <span className={fieldAdornment}>{suffix}</span> : null}
    </div>
  );
});
